import React, { useEffect, useRef, useState } from 'react';
import { performanceMetrics } from '../../data/mock';

const PerformanceMetrics = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedNumbers, setAnimatedNumbers] = useState(performanceMetrics.map(() => 0));
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          
          // Animate count-up
          const duration = 2000;
          const steps = 60;
          const interval = duration / steps;
          let step = 0;

          const parseValue = (val) => {
            const num = parseFloat(val.replace(/[^0-9.]/g, ''));
            return num;
          };

          const timer = setInterval(() => {
            step++;
            const progress = step / steps;
            const easeOut = 1 - Math.pow(1 - progress, 3);

            setAnimatedNumbers(
              performanceMetrics.map((m) => parseValue(m.value) * easeOut)
            );

            if (step >= steps) clearInterval(timer);
          }, interval);

          return () => clearInterval(timer);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const formatNumber = (num, originalValue) => {
    if (originalValue.includes('M')) {
      return `${num.toFixed(1)}M`;
    } else if (originalValue.includes('K')) {
      return `${Math.round(num)}K`;
    }
    return Math.round(num).toString();
  };

  return (
    <section
      id="results"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#D4AF37] text-xs tracking-[0.4em] mb-4">PERFORMANCE</p>
          <h2 className="font-playfair text-3xl md:text-4xl text-white">
            Numbers That <span className="italic text-[#D4AF37]">Speak</span>
          </h2>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {performanceMetrics.map((metric, index) => (
            <div
              key={metric.label}
              className={`group relative bg-[#121212] border border-[#D4AF37]/10 p-8 text-center transition-all duration-700 ease-out hover:border-[#D4AF37]/40 hover:shadow-[0_0_30px_rgba(212,175,55,0.1)] ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <p className="text-[#D4AF37] text-4xl md:text-5xl font-light mb-3 relative">
                {formatNumber(animatedNumbers[index], metric.value)}
                <span className="text-lg text-[#A1A1AA]">{metric.suffix}</span>
              </p>
              <p className="text-[#A1A1AA] text-xs tracking-[0.15em] relative">
                {metric.label.toUpperCase()}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PerformanceMetrics;
